/**
 * AssignedFormView — contact's view of an assigned form.
 *
 * Handles three aiMode variants:
 *   ai_only   → AIIntakeChat only
 *   toggle    → Switch between AI chat and manual form
 *   form_only → @formio/react form directly
 *
 * Submission paths:
 *   - Manual / Form.io path → POST /api/forms/submit (one submission).
 *   - AI path              → useAIIntake.confirmSubmit() POSTs /api/ai/intake/complete,
 *                            which creates the submission server-side (transcript +
 *                            summary derived from the session). That /complete call is
 *                            AUTHORITATIVE — AssignedFormView must NOT re-POST to
 *                            /api/forms/submit (doing so created a DUPLICATE submission).
 *                            The AI onSubmit callback only flips to the thank-you state.
 *
 * Shows a thank-you screen after successful submission.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { forms as formsApi, getAuthToken, ContactAssignmentDetail } from '../../lib/api.js';
import { flattenFormFields } from '../../lib/form-fields.js';
import { wireFormForUpload, setFormioToken, clearFormioToken } from '../../lib/formio-file-wiring.js';
import { scrollToFirstFormError, enableSubmitValidation } from '../../lib/formio-ux.js';
import AIIntakeChat from '../../components/chat/AIIntakeChat.js';
import { collectPrefillData } from '@clearintake/shared/identity';

interface Props {
  assignmentId: string;
}

type Mode = 'ai' | 'manual';

export default function AssignedFormView({ assignmentId }: Props): React.ReactElement {
  const [assignment,  setAssignment]  = useState<ContactAssignmentDetail | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [mode,       setMode]       = useState<Mode>('ai');
  const [submitted,  setSubmitted]  = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData,   setFormData]   = useState<Record<string, unknown>>({});
  const [FormComp,   setFormComp]   = useState<React.ComponentType<{
    form: unknown;
    onSubmit: (submission: { data: Record<string, unknown> }) => void;
    onError?: (errors: unknown) => void;
    options?: Record<string, unknown>;
  }> | null>(null);

  useEffect(() => {
    formsApi.assignments.getForContact(assignmentId).then((res) => {
      if (res.success && res.data) {
        setAssignment(res.data);
        setMode(res.data.aiMode === 'form_only' ? 'manual' : 'ai');
      } else {
        setError(res.error ?? 'Form not found');
      }
    }).catch(() => setError('Failed to load form')).finally(() => setLoading(false));
  }, [assignmentId]);

  // Lazy-load @formio/react
  useEffect(() => {
    import('@formio/react').then((mod) => {
      setFormComp(() => (mod as { Form: typeof FormComp }).Form);
    }).catch(() => { /* no formio */ });
  }, []);

  const handleManualSubmit = useCallback(async (submission: { data: Record<string, unknown> }) => {
    if (!assignment) return;
    setSubmitting(true);
    try {
      const res = await formsApi.submit({
        formId:      assignment.formId,
        assignmentId,
        locationId:  assignment.locationId,
        data:        submission.data,
      });
      if (res.success) setSubmitted(true);
      else setError(res.error ?? 'Submission failed');
    } catch (err) {
      setError(`Submission error: ${(err as Error).message ?? 'Network error — check API server'}`);
    } finally {
      setSubmitting(false);
    }
  }, [assignment, assignmentId]);

  // AI completion: the submission has ALREADY been created by the /complete
  // endpoint inside useAIIntake.confirmSubmit (transcript + summary stored
  // server-side). Do NOT POST to /api/forms/submit again — just show success.
  const handleAIComplete = useCallback(() => {
    setSubmitted(true);
  }, []);

  // Success screen
  if (submitted) {
    return (
      <div className="ci-portal" style={styles.thankYou}>
        <div style={styles.thankYouIcon}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--ci-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h2 style={styles.tyTitle}>Thank you</h2>
        <p style={styles.tySub}>Your intake has been submitted securely. Your care team has been notified and will review your information before your appointment.</p>
      </div>
    );
  }

  if (loading) return <div style={styles.center}><p style={styles.muted}>Loading form…</p></div>;
  if (error || !assignment) return <div className="ci-portal" style={styles.center}><p style={{ color: '#c0392b', fontFamily: F }}>{error ?? 'Form not found'}</p></div>;

  return (
    <div className="ci-portal" style={styles.page}>
      {/* Secure banner */}
      <div style={styles.secureBanner}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        Secure &amp; Encrypted Form
      </div>

      <div style={styles.container}>
        <h1 style={styles.formTitle}>{assignment.formName}</h1>
        {assignment.description && <p style={styles.formDesc}>{assignment.description}</p>}

        {/* Mode toggle for "toggle" aiMode */}
        {assignment.aiMode === 'toggle' && (
          <div style={styles.modeToggle}>
            <button
              className="ci-mode-btn"
              data-active={mode === 'ai'}
              style={{ ...styles.modeBtn, ...(mode === 'ai' ? styles.modeBtnActive : {}) }}
              onClick={() => setMode('ai')}
            >
              Chat with AI
            </button>
            <button
              className="ci-mode-btn"
              data-active={mode === 'manual'}
              style={{ ...styles.modeBtn, ...(mode === 'manual' ? styles.modeBtnActive : {}) }}
              onClick={() => setMode('manual')}
            >
              Fill form myself
            </button>
          </div>
        )}

        {error && <div style={styles.errorBox} role="alert">{error}</div>}

        {/* Render based on mode */}
        {(mode === 'ai' && assignment.aiMode !== 'form_only') ? (
          <AIIntakeChat
            formId={assignment.formId}
            assignmentId={assignmentId}
            locationId={assignment.locationId}
            authToken={getAuthToken() ?? undefined}
            formFields={flattenFormFields(
              (assignment.schemaJson as { components?: Parameters<typeof flattenFormFields>[0] } | undefined)?.components,
            )}
            onSubmit={handleAIComplete}
            onSwitchToForm={assignment.aiMode === 'toggle'
            ? (collectedData) => {
                setFormData(collectedData);
                setMode('manual');
              }
            : undefined}
          />
        ) : (
          <ManualFormView
            schemaJson={assignment.schemaJson}
            formId={assignment.formId}
            locationId={assignment.locationId}
            FormComp={FormComp}
            onSubmit={handleManualSubmit}
            submitting={submitting}
            formData={formData}
            setFormData={setFormData}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual form view
// ---------------------------------------------------------------------------

function ManualFormView({
  schemaJson, formId, locationId, FormComp, onSubmit, submitting, formData, setFormData,
}: {
  schemaJson: unknown;
  formId: string;
  locationId: string;
  FormComp: React.ComponentType<{
    form: unknown;
    submission?: { data: Record<string, unknown> };
    onSubmit: (s: { data: Record<string, unknown> }) => void;
    onError?: (errors: unknown) => void;
    options?: Record<string, unknown>;
  }> | null;
  onSubmit: (submission: { data: Record<string, unknown> }) => void;
  submitting: boolean;
  formData: Record<string, unknown>;
  setFormData: (d: Record<string, unknown>) => void;
}): React.ReactElement {
  // Wire file components to the upload endpoint ONCE (re-wiring each render would
  // re-initialise the Form.io instance and drop in-progress input).
  const wiredSchema = useMemo(
    () => (schemaJson ? enableSubmitValidation(wireFormForUpload(schemaJson, formId, locationId)) : schemaJson),
    [schemaJson, formId, locationId],
  );
  // Route date-family prefill (DOB) through Form.io submission data (server-substituted
  // schema arrives with the resolved defaultValue; a datetime widget won't paint it directly).
  const prefill = useMemo(() => ({ data: collectPrefillData(wiredSchema) }), [wiredSchema]);
  // Put the patient's contact JWT into Form.io's token store so file uploads carry
  // x-jwt-token (the endpoint requires auth for non-public forms). Cleared on unmount.
  useEffect(() => {
    setFormioToken();
    return () => clearFormioToken();
  }, []);
  if (FormComp && schemaJson) {
    return (
      <div className="ci-formio-form" style={styles.formioWrap}>
        <FormComp
          form={wiredSchema}
          submission={prefill}
          onSubmit={onSubmit}
          onError={() => scrollToFirstFormError()}
          options={{}}
        />
        {submitting && <p style={styles.muted}>Submitting…</p>}
      </div>
    );
  }

  // Fallback: dynamic text inputs from schema components
  const components = ((schemaJson as { components?: { key: string; label: string; type: string }[] })?.components ?? []);
  return (
    <div style={styles.fallbackForm}>
      {components.map((c) => (
        <div key={c.key} style={styles.fallbackField}>
          <label style={styles.fallbackLabel}>{c.label}</label>
          <input
            className="ci-focusable"
            style={styles.input}
            value={String(formData[c.key] ?? '')}
            onChange={(e) => setFormData({ ...formData, [c.key]: e.target.value })}
          />
        </div>
      ))}
      <button
        style={{ ...styles.submitBtn, opacity: submitting ? 0.7 : 1 }}
        onClick={() => onSubmit({ data: formData })}
        disabled={submitting}
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>
    </div>
  );
}

const F = 'var(--ci-font)';

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight:  '100vh',
    background: 'var(--ci-surface)',
    fontFamily: F,
  },
  secureBanner: {
    background:     '#1a2744',
    color:          'rgba(255,255,255,0.82)',
    padding:        '9px 20px',
    fontSize:       12,
    textAlign:      'center',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            6,
    letterSpacing:  '0.01em',
    fontFamily:     F,
  },
  container:   { maxWidth: 960, margin: '0 auto', padding: '24px 24px 48px' },
  formTitle:   { fontSize: 22, fontWeight: 700, color: 'var(--ci-text)', margin: '0 0 8px', fontFamily: F },
  formDesc:    { fontSize: 15, color: 'var(--ci-text-muted)', lineHeight: 1.65, margin: '0 0 20px', fontFamily: F },
  modeToggle:  { display: 'flex', gap: 8, marginBottom: 20 },
  modeBtn: {
    flex:         1,
    padding:      '12px',
    minHeight:    44,
    border:       '2px solid var(--ci-border)',
    borderRadius: 8,
    background:   '#ffffff',
    cursor:       'pointer',
    fontSize:     14,
    fontWeight:   500,
    color:        'var(--ci-text-body)',
    fontFamily:   F,
    transition:   'border-color 0.15s, color 0.15s, background 0.15s',
  },
  modeBtnActive: {
    borderColor: 'var(--ci-primary)',
    color:       'var(--ci-primary)',
    background:  'var(--ci-primary-light)',
  },
  errorBox: {
    background:   '#fef4f2',
    border:       '1px solid #f5c6bc',
    borderRadius: 8,
    padding:      '10px 14px',
    color:        '#c0392b',
    fontSize:     13,
    marginBottom: 12,
    fontFamily:   F,
  },
  center: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    minHeight:      '100vh',
    background:     'var(--ci-surface)',
  },
  muted: { color: 'var(--ci-text-muted)', fontSize: 14, fontFamily: F },
  thankYou: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    minHeight:      '100vh',
    gap:            16,
    fontFamily:     F,
    background:     'var(--ci-surface)',
  },
  thankYouIcon: {
    width:          72,
    height:         72,
    borderRadius:   '50%',
    background:     'var(--ci-primary-light)',
    border:         '1px solid var(--ci-border)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
  },
  tyTitle: { fontSize: 24, fontWeight: 700, color: 'var(--ci-text)', margin: 0, fontFamily: F },
  tySub:   { fontSize: 16, color: 'var(--ci-text-muted)', margin: 0, fontFamily: F, textAlign: 'center', maxWidth: 340, lineHeight: 1.6 },
  submitBtn: {
    width:        '100%',
    padding:      '12px',
    minHeight:    46,
    background:   'var(--ci-primary)',
    color:        '#fff',
    border:       'none',
    borderRadius: 8,
    fontSize:     15,
    fontWeight:   600,
    cursor:       'pointer',
    fontFamily:   F,
    letterSpacing: '0.01em',
    transition:   'background 0.15s',
  },
  formioWrap:    { background: '#ffffff', borderRadius: 14, padding: '24px', border: '1px solid var(--ci-border)' },
  fallbackForm:  { background: '#ffffff', borderRadius: 14, padding: '24px', border: '1px solid var(--ci-border)' },
  fallbackField: { marginBottom: 16 },
  fallbackLabel: { display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ci-text-body)', marginBottom: 6, fontFamily: F },
  input: {
    width:        '100%',
    padding:      '11px 12px',
    minHeight:    44,
    border:       '1px solid var(--ci-border)',
    borderRadius: 8,
    fontSize:     15,
    color:        'var(--ci-text)',
    boxSizing:    'border-box' as const,
    fontFamily:   F,
    outline:      'none',
    transition:   'border-color 0.15s',
  },
};
